import { collection, doc, onSnapshot, serverTimestamp, setDoc, type Unsubscribe } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "./firebase";

export type CalendarProvider = "apple" | "google" | "microsoft" | "ical";
export type CalendarAccessMode = "read-only" | "read-write";
export type CalendarDetailVisibility = "full" | "title-only" | "busy-only";
export type CalendarConnectionStatus = "pending" | "active" | "attention" | "disconnected";

export type CalendarConnection = {
  id: string;
  provider: CalendarProvider;
  displayName: string;
  ownerUid: string;
  accessMode: CalendarAccessMode;
  detailVisibility: CalendarDetailVisibility;
  status: CalendarConnectionStatus;
  isDefaultDestination: boolean;
  canCreateEvents: boolean;
  lastSyncedAt?: { toDate?: () => Date } | null;
};

export type CalendarEventLink = {
  id: string;
  canonicalEventId: string;
  sourceConnectionId: string | null;
  destinationConnectionId: string;
  destinationExternalEventId: string | null;
  destinationTitle: string;
  busy: boolean;
  includeDetails: boolean;
  keepSynchronized: boolean;
  status: "pending" | "synced" | "attention" | "removed";
};

export function subscribeToCalendarConnections(
  householdId: string,
  onConnections: (connections: CalendarConnection[]) => void,
  onError: () => void
): Unsubscribe {
  return onSnapshot(
    collection(db, "households", householdId, "calendarConnections"),
    (snapshot) => {
      const connections = snapshot.docs
        .map((connection) => ({
          id: connection.id,
          ...connection.data()
        } as CalendarConnection))
        .sort((left, right) => {
          if (left.isDefaultDestination !== right.isDefaultDestination) {
            return left.isDefaultDestination ? -1 : 1;
          }
          return left.displayName.localeCompare(right.displayName);
        });
      onConnections(connections);
    },
    onError
  );
}

export function writableCalendarConnections(connections: CalendarConnection[]) {
  return connections.filter(
    (connection) =>
      connection.status === "active" &&
      connection.accessMode === "read-write" &&
      connection.canCreateEvents
  );
}

export async function requestLinkedCalendarCopy(
  householdId: string,
  canonicalEventId: string,
  connection: CalendarConnection,
  userId: string,
  options: {
    destinationTitle: string;
    busy: boolean;
    includeDetails: boolean;
    keepSynchronized: boolean;
  }
) {
  const linkRef = doc(collection(db, "households", householdId, "calendarEventLinks"));
  await setDoc(linkRef, {
    canonicalEventId,
    sourceConnectionId: null,
    destinationConnectionId: connection.id,
    destinationExternalEventId: null,
    destinationTitle: options.destinationTitle.trim(),
    busy: options.busy,
    includeDetails: options.includeDetails,
    keepSynchronized: options.keepSynchronized,
    status: "pending",
    createdBy: userId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
}


export type AppleCalendarChoice = {
  key: string;
  name: string;
  description?: string;
};

export async function startAppleCalendarConnection(
  householdId: string,
  username: string,
  appSpecificPassword: string
) {
  const call = httpsCallable<
    { householdId: string; username: string; appSpecificPassword: string },
    { attemptId: string; calendars: AppleCalendarChoice[] }
  >(functions, "startAppleCalendarConnection");
  return (await call({ householdId, username, appSpecificPassword })).data;
}

export async function completeAppleCalendarConnection(
  householdId: string,
  attemptId: string,
  calendarKey: string
) {
  const call = httpsCallable<
    { householdId: string; attemptId: string; calendarKey: string },
    { connectionId: string; displayName: string }
  >(functions, "completeAppleCalendarConnection");
  return (await call({ householdId, attemptId, calendarKey })).data;
}

export async function syncAppleCalendar(householdId: string, connectionId: string) {
  const call = httpsCallable<
    { householdId: string; connectionId: string },
    { imported: number }
  >(functions, "syncAppleCalendar");
  return (await call({ householdId, connectionId })).data;
}

export async function disconnectAppleCalendar(householdId: string, connectionId: string) {
  const call = httpsCallable<
    { householdId: string; connectionId: string },
    { disconnected: boolean }
  >(functions, "disconnectAppleCalendar");
  return (await call({ householdId, connectionId })).data;
}


export type GoogleCalendarChoice = {
  id: string;
  name: string;
  primary: boolean;
  accessRole: string;
  canCreateEvents: boolean;
};

export async function startGoogleCalendarConnection(householdId: string, returnUrl: string) {
  const call = httpsCallable<
    { householdId: string; returnUrl: string },
    { authUrl: string }
  >(functions, "startGoogleCalendarConnection");
  return (await call({ householdId, returnUrl })).data;
}

export async function getGoogleCalendarConnectionAttempt(householdId: string, attemptId: string) {
  const call = httpsCallable<
    { householdId: string; attemptId: string },
    { calendars: GoogleCalendarChoice[] }
  >(functions, "getGoogleCalendarConnectionAttempt");
  return (await call({ householdId, attemptId })).data;
}

export async function finalizeGoogleCalendarConnection(
  householdId: string,
  attemptId: string,
  calendarId: string
) {
  const call = httpsCallable<
    { householdId: string; attemptId: string; calendarId: string },
    { connectionId: string; displayName: string; canCreateEvents: boolean }
  >(functions, "finalizeGoogleCalendarConnection");
  return (await call({ householdId, attemptId, calendarId })).data;
}

export async function syncGoogleCalendar(householdId: string, connectionId: string) {
  const call = httpsCallable<
    { householdId: string; connectionId: string },
    { imported: number }
  >(functions, "syncGoogleCalendar");
  return (await call({ householdId, connectionId })).data;
}

export async function disconnectGoogleCalendar(householdId: string, connectionId: string) {
  const call = httpsCallable<
    { householdId: string; connectionId: string },
    { disconnected: boolean }
  >(functions, "disconnectGoogleCalendar");
  return (await call({ householdId, connectionId })).data;
}
